import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { AdsManagerOnboardingPublicLandingPageButton } from "@oai/analytics/gen/client-events/protobuf_analytics_events/v1/tapestry";
import { ButtonLink } from "@oai/platform/ui/Button";
import { ArrowUpRight } from "@oai/platform/ui/Icon";

import OpenAIWordmark from "@/assets/openai-wordmark.svg?react";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { captureGoogleAdsClickIdFromCurrentUrl } from "@/lib/googleAdsMeasurement";
import { captureLinkedInAdsClickIdFromCurrentUrl } from "@/lib/linkedInAdsMeasurement";
import { captureMetaAdsClickIdFromCurrentUrl } from "@/lib/metaAdsMeasurement";
import { captureRedditAdsClickIdFromCurrentUrl } from "@/lib/redditAdsMeasurement";
import { DEFAULT_LOCALE, getLocalizedLandingPath, sanitizeLocale } from "@/locale";
import { messages } from "@/localization/Landing.messages";

export function LandingNav({
  ctaText,
  onCtaClick,
}: {
  ctaText: string;
  onCtaClick: (button: AdsManagerOnboardingPublicLandingPageButton) => void;
}) {
  const intl = useIntl();
  const location = useLocation();
  const selectedLocale = sanitizeLocale(intl.locale) ?? DEFAULT_LOCALE;
  const { isConsentResolved, marketingConsentEnabled } = useCookieConsent();
  const captureClickIdIfAllowed = () => {
    if (location.search && isConsentResolved && marketingConsentEnabled) {
      captureGoogleAdsClickIdFromCurrentUrl();
      captureLinkedInAdsClickIdFromCurrentUrl();
      captureMetaAdsClickIdFromCurrentUrl();
      captureRedditAdsClickIdFromCurrentUrl();
    }
  };

  return (
    <header className="bg-surface fixed inset-x-0 top-0 z-50">
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center justify-between px-6">
        <a
          href={getLocalizedLandingPath(selectedLocale)}
          className="text-primary inline-flex items-center"
          aria-label={intl.formatMessage(messages.openAiHome)}
        >
          <OpenAIWordmark className="w-[79px] md:w-[90px]" aria-hidden="true" focusable="false" />
        </a>
        <div className="flex items-center gap-2">
          <ButtonLink
            href="/auth/login"
            variant="outline"
            as="a"
            size="lg"
            pill
            onClick={() => {
              captureClickIdIfAllowed();
              onCtaClick(
                AdsManagerOnboardingPublicLandingPageButton.ADS_MANAGER_ONBOARDING_PUBLIC_LANDING_PAGE_BUTTON_LOG_IN,
              );
            }}
          >
            {intl.formatMessage(messages.logIn)}
          </ButtonLink>
          <ButtonLink
            href="/auth/signup"
            as="a"
            color="primary"
            variant="solid"
            size="lg"
            pill
            onClick={() => {
              captureClickIdIfAllowed();
              onCtaClick(
                AdsManagerOnboardingPublicLandingPageButton.ADS_MANAGER_ONBOARDING_PUBLIC_LANDING_PAGE_BUTTON_SIGN_UP,
              );
            }}
          >
            {ctaText || intl.formatMessage(messages.signUp)}
            <ArrowUpRight />
          </ButtonLink>
        </div>
      </div>
    </header>
  );
}
